import { getMockDb } from "@/mock-api/db.ts";
import type { StandardRequest } from "@/mock-api/msw/StandardRequest.ts";
import { http } from "@/mock-api/msw/handlerMiddleware.ts";
import { getLocationParam, getQueryParams, pagedResponse } from "@/mock-api/requestUtils.ts";
import { standardErrorResponse } from "@/mock-api/responses.ts";
import { BillingAccountStatusEventType } from "@warrenio/api-spec/spec/billing";
import { mustGet } from "@warrenio/utils/collections/mustGet";
import { type GraphQLHandler, type HttpHandler, HttpResponse } from "msw";
import { first } from "remeda";

/* eslint-disable @typescript-eslint/require-await */

//
// NB: We use dynamic imports in handlers (for large example modules) to improve development load times.
//

export const adminHandlers: (HttpHandler | GraphQLHandler)[] = [
    //#region Admin data
    http.get("/payment/admin/campaign/list", async (_r) => {
        return HttpResponse.json(getMockDb().campaigns);
    }),

    http.get("/payment/admin/referral_enabled", async (_r) => {
        return HttpResponse.json(getMockDb().referral_enabled);
    }),

    http.get("/config/payment/referral_amounts", async (_r) => {
        return HttpResponse.json(getMockDb().referral_amounts);
    }),

    http.get("/config/ui/gtm_key", async (_r) => {
        return HttpResponse.json(getMockDb().gtm_key);
    }),

    // Request URL: http://localhost:5173/v1/tll/base-operator/admin/action_log
    http.get("/{location}/base-operator/admin/action_log", async (r) => {
        const query = getQueryParams(r);
        const guest_uuid = mustGet(query, "guest_uuid", "query");
        const { defaultHostUuid } = await import("@/mock-api/data/adminHostExample.ts");
        const data = [
            {
                action: "migrate_to",
                created_at: "2025-12-12 10:15:52",
                guest_uuid,
                acting_user_id: 9,
                host_uuid: defaultHostUuid,
                hypervisor_uuid: defaultHostUuid,
            },
            {
                action: "migrate_from",
                created_at: "2025-12-12 10:15:52",
                guest_uuid,
                acting_user_id: 9,
                host_uuid: "00000000-0000-0000-0000-000167bb0a39",
                hypervisor_uuid: "00000000-0000-0000-0000-000167bb0a39",
            },
            {
                action: "create",
                created_at: "2025-12-12 09:56:13",
                guest_uuid,
                acting_user_id: 9,
                host_uuid: defaultHostUuid,
                hypervisor_uuid: defaultHostUuid,
            },
        ];

        return HttpResponse.json(data);
    }),

    // Request URL: http://localhost:5173/v1/tll/base-operator/admin/host_pool/list
    http.get("/{location}/base-operator/admin/host_pool/list", async (r) => {
        const { adminHostPoolExample, adminHostPoolExample2 } = await import("@/mock-api/data/adminHostPoolExample.ts");
        const location = getLocationParam(r);
        if (location === "tll") {
            return HttpResponse.json(adminHostPoolExample);
        }
        return HttpResponse.json(adminHostPoolExample2);
    }),

    // Request URL: http://localhost:5173/v1/tll/base-operator/host/list
    http.get("/{location}/base-operator/host/list", async (r) => {
        const { adminHostExample, adminHostExample2 } = await import("@/mock-api/data/adminHostExample.ts");
        const location = getLocationParam(r);
        if (location === "tll") {
            return HttpResponse.json(adminHostExample);
        }
        return HttpResponse.json(adminHostExample2);
    }),

    // Request URL: http://localhost:5173/v1/tll/base-operator/admin/vm/statistics/chart
    http.get("/{location}/base-operator/admin/vm/statistics/chart", async (r) => {
        const { adminVmStatisticsExample, adminVmStatisticsExample2 } =
            await import("@/mock-api/data/adminVmStatisticsExample.ts");
        const location = getLocationParam(r);
        if (location === "tll") {
            return HttpResponse.json(adminVmStatisticsExample);
        }
        return HttpResponse.json(adminVmStatisticsExample2);
    }),

    // Request URL: http://localhost:5173/v1/payment/admin/billing_account_stats
    http.get("/payment/admin/billing_account_stats", async (_r) => {
        const { adminBillingAccountStatsExample } = await import("@/mock-api/data/adminUserConversionExample.ts");
        return HttpResponse.json(adminBillingAccountStatsExample);
    }),

    // Request URL: http://localhost:5173/v1/payment/admin/billing_account/status_history
    http.get("/payment/admin/billing_account/status_history", async (r) => {
        const query = getQueryParams(r);
        const baId = Number(mustGet(query, "billing_account_id", "query"));
        const eventType = query.get("event_type");
        if (eventType != null && !BillingAccountStatusEventType.safeParse(eventType).success) {
            return standardErrorResponse(`Unknown event type '${eventType}'.`, 400);
        }

        const events = getMockDb().billingStatusHistory.filter(
            (e) => e.billing_account_id === baId && (eventType == null || e.event_type === eventType),
        );
        return pagedResponse(r, events);
    }),

    // Request URL: http://localhost:5173/v1/user-resource/admin/user_signups
    http.get("/user-resource/admin/user_signups", async (_r) => {
        const { adminUserSignupsStatsExample } = await import("@/mock-api/data/adminUserConversionExample.ts");
        return HttpResponse.json(adminUserSignupsStatsExample);
    }),

    // Request URL: http://localhost:5173/v1/charging/admin/monthly_subtotals
    http.get("/charging/admin/monthly_subtotals", async (_r) => {
        const { adminMonthlySubtotalsExample } = await import("@/mock-api/data/adminMonthlySubtotalsExample.ts");
        return HttpResponse.json(adminMonthlySubtotalsExample);
    }),

    // Request URL: http://localhost:5173/v1/payment/admin/invoiced_revenue
    http.get("/payment/admin/invoiced_revenue", async (_r) => {
        const { adminInvoiceTotalsExample } = await import("@/mock-api/data/adminInvoiceTotalsExample.ts");
        return HttpResponse.json(adminInvoiceTotalsExample);
    }),

    // Request URL: http://localhost:5173/v1/payment/admin/monthly_arpa
    http.get("/payment/admin/monthly_arpa", async (_r) => {
        const { adminMonthlyArpaExample } = await import("@/mock-api/data/adminMonthlyArpaExample.ts");
        return HttpResponse.json(adminMonthlyArpaExample);
    }),

    // Request URL: http://localhost:5173/v1/user-resource/admin/service/packages
    http.get("/user-resource/admin/service/packages", async (_r) => {
        const { makeServicePackageExample } = await import("@/mock-api/data/adminServicePackagesExample.ts");
        return HttpResponse.json(makeServicePackageExample(getMockDb().locations[0].slug));
    }),

    // Request URL: http://localhost:5173/v1/storage/admin/pools
    http.get("/{location}/storage/admin/pools", async (r) => {
        const { adminStoragePoolExample } = await import("@/mock-api/data/adminStoragePoolExample.ts");
        const location = getLocationParam(r);
        if (location === "tll") {
            return HttpResponse.json(adminStoragePoolExample);
        }
        return HttpResponse.json([]);
    }),

    // Request URL: http://localhost:5173/v1/network/admin/ip_pool_status
    http.get("/{location}/network/admin/ip_pool_status", async (_r) => {
        const { adminIpPoolExample } = await import("@/mock-api/data/adminIpPoolExample.ts");
        return HttpResponse.json(adminIpPoolExample);
    }),

    // Request URL: http://localhost:5173/v1/config/payment
    http.get("/config/payment", async (_r) => {
        const { adminConfigPaymentExample } = await import("@/mock-api/data/adminConfigExample.ts");
        return HttpResponse.json(adminConfigPaymentExample);
    }),

    // Request URL: http://localhost:5173/v1/config/compute/limits/vm
    http.get("/config/compute/limits/vm", async (_r) => {
        const { adminConfigVmExample } = await import("@/mock-api/data/adminConfigExample.ts");
        return HttpResponse.json(adminConfigVmExample);
    }),

    // Request URL: http://localhost:5173/v1/config/network/iftune
    http.get("/config/network/iftune", async (_r) => {
        const { adminNetworkIftuneExample } = await import("@/mock-api/data/adminNetworkIftuneExample.ts");
        return HttpResponse.json(adminNetworkIftuneExample);
    }),

    // Request URL: http://localhost:5173/v1/charging/admin/products
    http.get("/charging/admin/products", async (_r) => {
        const { adminCustomProductsExample } = await import("@/mock-api/data/adminCustomProductsExample.ts");
        return HttpResponse.json(adminCustomProductsExample);
    }),

    // Request URL: http://localhost:5173/v1/user-resource/admin/admin_consumers
    http.get("/user-resource/admin/elevated_users", async (_r) => {
        const { adminAdminUsersExample } = await import("@/mock-api/data/adminAdminUsersExample.ts");
        return HttpResponse.json(adminAdminUsersExample);
    }),

    // Request URL: http://localhost:5173/v1/configuration/admin/value
    http.get("/configuration/admin/value", async (r) => {
        const { adminConfigDataExample } = await import("@/mock-api/data/adminConfigDataExample.ts");
        const path = getConfigPathQuery(r).replace(/^\//, "");
        const config = adminConfigDataExample.find((item) => item.section_key === path);
        const data = config?.data;

        if (!config) return HttpResponse.json("");

        if (Array.isArray(data)) {
            const item: any = first(data);
            if ("section_key" in item) {
                return HttpResponse.json(data);
            } else {
                return HttpResponse.json(JSON.stringify(data, null, 2));
            }
        } else if (typeof data === "object") {
            return HttpResponse.json(JSON.stringify(data, null, 2));
        } else if (typeof data === "string") {
            return HttpResponse.json(data);
        } else if (typeof data === "boolean") {
            return HttpResponse.json(data.toString());
        } else if (typeof data === "number") {
            return HttpResponse.json(data.toString());
        }

        return HttpResponse.json("");
    }),
];

//#region Admin config-specific accessors
function getConfigPathQuery(r: StandardRequest) {
    const query = getQueryParams(r);
    return String(mustGet(query, "path", "query"));
}
//#endregion
